/**
 * Settings.tsx — Cloud credential management.
 *
 * Shows the permission level (scan / remediation) per provider and
 * lets customers add or rotate remediation credentials without going
 * through the full onboarding wizard.
 */

import React, { useEffect, useRef, useState } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Collapse from '@mui/material/Collapse';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';

import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloudIcon from '@mui/icons-material/Cloud';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DownloadIcon from '@mui/icons-material/Download';
import LockIcon from '@mui/icons-material/Lock';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';

import axios from 'axios';
import API_BASE from '../config/api';
import { useAuth } from '../context/AuthContext';
import AutoFixConsentModal from '../components/AutoFixConsentModal';

const PROVIDERS = ['aws', 'gcp', 'azure', 'ssh'] as const;
type Provider = typeof PROVIDERS[number];

const PROVIDER_LABELS: Record<Provider, string> = {
  aws:   'AWS',
  gcp:   'Google Cloud',
  azure: 'Microsoft Azure',
  ssh:   'Private Server (SSH)',
};

const PROVIDER_COLORS: Record<Provider, string> = {
  aws:   '#f77f00',
  gcp:   '#2dc653',
  azure: '#00b4d8',
  ssh:   '#9c6ade',
};

interface RemediationStatus {
  remediation_enabled: boolean;
  providers: Record<string, boolean>;
}

// ── Credential forms ──────────────────────────────────────────────────────────

function AWSForm({ customerId, onSave }: { customerId: string; onSave: (result: { success: boolean; message: string }) => void }) {
  const [step, setStep] = useState<'idle' | 'waiting' | 'done'>('idle');
  const [consentOpen, setConsentOpen] = useState(false);
  const [consentLoading, setConsentLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [externalId, setExternalId] = useState('');
  const [stackName, setStackName] = useState('');
  const [launchStackUrl, setLaunchStackUrl] = useState('');
  const [roleArn, setRoleArn] = useState('');
  const [error, setError] = useState<string | null>(null);

  async function handleConsentAccepted() {
    setConsentLoading(true);
    setError(null);
    try {
      await axios.post(`${API_BASE}/onboarding/aws/record-consent/authenticated`);
      setConsentOpen(false);
      const { data } = await axios.post(`${API_BASE}/onboarding/aws/init-connection/authenticated`, {
        purpose: 'remediation',
      });
      setExternalId(data.external_id);
      setStackName(data.stack_name);
      setLaunchStackUrl(data.launch_stack_url || '');
      setStep('waiting');
    } catch (e: any) {
      setError(e.response?.data?.detail ?? 'Failed to initialize.');
    } finally {
      setConsentLoading(false);
    }
  }

  async function handleConfirm() {
    if (!roleArn.startsWith('arn:aws:iam::')) {
      setError('Please enter a valid IAM Role ARN.');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const { data } = await axios.post(`${API_BASE}/onboarding/aws/confirm-connection/authenticated`, {
        role_arn: roleArn,
        purpose: 'remediation',
      });
      if (data.success) {
        setStep('done');
        onSave({ success: true, message: data.message });
      } else {
        setError(data.message || 'Verification failed.');
      }
    } catch (e: any) {
      setError(e.response?.data?.detail ?? 'Verification failed.');
    } finally {
      setLoading(false);
    }
  }

  if (step === 'done') {
    return <Alert severity="success" sx={{ mt: 1 }}>Remediation role connected. Auto-Fix is ready for AWS.</Alert>;
  }

  if (step === 'waiting') {
    return (
      <Stack spacing={2}>
        <Alert severity="info">
          Deploy the remediation stack in AWS, then paste the Role ARN below.
        </Alert>
        <Box sx={{ bgcolor: 'rgba(0,180,216,0.05)', borderRadius: 2, p: 1.5 }}>
          <Typography variant="caption" color="text.secondary">Stack Name</Typography>
          <Typography variant="body2" fontFamily="monospace">{stackName}</Typography>
          <Typography variant="caption" color="text.secondary" mt={1} display="block">ExternalId</Typography>
          <Typography variant="body2" fontFamily="monospace" sx={{ wordBreak: 'break-all' }}>{externalId}</Typography>
        </Box>
        <Button
          component="a"
          href={launchStackUrl}
          target="_blank"
          rel="noopener noreferrer"
          variant="contained"
          size="small"
          startIcon={<OpenInNewIcon />}
          sx={{ fontWeight: 700 }}
        >
          Launch Stack in AWS Console
        </Button>
        <TextField
          label="Remediation Role ARN"
          fullWidth
          size="small"
          value={roleArn}
          onChange={(e) => setRoleArn(e.target.value)}
          placeholder="arn:aws:iam::123456789012:role/shield-remediation-role"
        />
        <Button variant="contained" size="small" disabled={!roleArn || loading} onClick={handleConfirm}>
          {loading ? 'Verifying...' : 'Verify Remediation Role'}
        </Button>
        {error && <Alert severity="error">{error}</Alert>}
      </Stack>
    );
  }

  return (
    <Stack spacing={2}>
      <Button
        variant="contained"
        size="small"
        onClick={() => setConsentOpen(true)}
        sx={{ fontWeight: 700 }}
      >
        Launch Remediation Stack in AWS
      </Button>
      {error && <Alert severity="error">{error}</Alert>}
      <AutoFixConsentModal
        open={consentOpen}
        loading={consentLoading}
        onAccept={handleConsentAccepted}
        onCancel={() => setConsentOpen(false)}
      />
    </Stack>
  );
}

function GCPForm({ onSave }: { onSave: (c: Record<string, string>) => void }) {
  const [pid, setPid] = useState(''); const [kj, setKj] = useState('');
  const ref = useRef<HTMLInputElement>(null);
  function handleFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]; if (!file) return;
    const r = new FileReader();
    r.onload = ev => { try { const p = JSON.parse(ev.target?.result as string); setKj(JSON.stringify(p)); if (!pid) setPid(p.project_id || ''); } catch {} };
    r.readAsText(file);
  }
  return (
    <Stack spacing={2}>
      <TextField label="Project ID" fullWidth size="small" value={pid} onChange={e => setPid(e.target.value)} />
      <input ref={ref} type="file" accept=".json" style={{ display: 'none' }} onChange={handleFile} />
      <Button variant="outlined" size="small" fullWidth onClick={() => ref.current?.click()}>{kj ? '✅ JSON key loaded' : 'Upload service account JSON key'}</Button>
      <Button variant="contained" size="small" disabled={!pid || !kj} onClick={() => onSave({ project_id: pid, key_json: kj })}>
        Test & Save
      </Button>
    </Stack>
  );
}

function AzureForm({ onSave }: { onSave: (c: Record<string, string>) => void }) {
  const [ti, setTi] = useState(''); const [ci, setCi] = useState('');
  const [sec, setSec] = useState(''); const [sub, setSub] = useState('');
  const [show, setShow] = useState(false);
  return (
    <Stack spacing={2}>
      <TextField label="Tenant ID" fullWidth size="small" value={ti} onChange={e => setTi(e.target.value)} />
      <TextField label="Client ID" fullWidth size="small" value={ci} onChange={e => setCi(e.target.value)} />
      <TextField label="Client Secret" fullWidth size="small" type={show ? 'text' : 'password'} value={sec} onChange={e => setSec(e.target.value)}
        InputProps={{ endAdornment: <InputAdornment position="end"><IconButton size="small" onClick={() => setShow(p => !p)}>{show ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}</IconButton></InputAdornment> }} />
      <TextField label="Subscription ID" fullWidth size="small" value={sub} onChange={e => setSub(e.target.value)} />
      <Button variant="contained" size="small" disabled={!ti || !ci || !sec || !sub} onClick={() => onSave({ tenant_id: ti, client_id: ci, client_secret: sec, subscription_id: sub })}>
        Test & Save
      </Button>
    </Stack>
  );
}

function SSHForm({ onSave }: { onSave: (c: Record<string, string>) => void }) {
  const [host, setHost] = useState(''); const [port, setPort] = useState('22');
  const [user, setUser] = useState(''); const [key, setKey] = useState('');
  const ref = useRef<HTMLInputElement>(null);
  function handleFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]; if (!file) return;
    const r = new FileReader(); r.onload = ev => setKey(ev.target?.result as string); r.readAsText(file);
  }
  return (
    <Stack spacing={2}>
      <TextField label="Host / IP" fullWidth size="small" value={host} onChange={e => setHost(e.target.value)} />
      <TextField label="Port" fullWidth size="small" type="number" value={port} onChange={e => setPort(e.target.value)} />
      <TextField label="Username" fullWidth size="small" value={user} onChange={e => setUser(e.target.value)} />
      <input ref={ref} type="file" accept=".pem,.key" style={{ display: 'none' }} onChange={handleFile} />
      <Button variant="outlined" size="small" fullWidth onClick={() => ref.current?.click()}>{key ? '✅ Key loaded' : 'Upload SSH private key'}</Button>
      <Button variant="contained" size="small" disabled={!host || !user || !key} onClick={() => onSave({ host, port, username: user, private_key: key })}>
        Test & Save
      </Button>
    </Stack>
  );
}

// ── Provider row ─────────────────────────────────────────────────────────────

function ProviderRow({
  provider,
  hasRemediation,
  customerId,
  onSaved,
}: {
  provider: Provider;
  hasRemediation: boolean;
  customerId: string;
  onSaved: () => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const [saving,   setSaving]   = useState(false);
  const [result,   setResult]   = useState<{ success: boolean; message: string } | null>(null);

  async function handleSave(creds: Record<string, string>) {
    setSaving(true);
    setResult(null);
    try {
      const { data } = await axios.post(
        `${API_BASE}/v1/autofix/${customerId}/remediation-credentials`,
        { provider, credentials: creds },
      );
      setResult({ success: data.success, message: data.message });
      if (data.success) { setExpanded(false); onSaved(); }
    } catch (e: any) {
      setResult({ success: false, message: e.response?.data?.detail ?? 'Failed to save credentials' });
    } finally {
      setSaving(false);
    }
  }

  const policyUrl = `${API_BASE}/onboarding/policy-template/${provider}`;

  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 1.5 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1}>
        <Box display="flex" alignItems="center" gap={1.5}>
          <CloudIcon sx={{ color: PROVIDER_COLORS[provider] }} />
          <Box>
            <Typography variant="body1" fontWeight={600}>{PROVIDER_LABELS[provider]}</Typography>
            <Stack direction="row" spacing={0.5} mt={0.3}>
              <Chip label="Scan (read-only)" size="small" color="info" variant="outlined" icon={<CheckCircleIcon sx={{ fontSize: 12 }} />} />
              {hasRemediation
                ? <Chip label="Auto-Fix enabled" size="small" color="success" icon={<CheckCircleIcon sx={{ fontSize: 12 }} />} />
                : <Chip label="Auto-Fix not configured" size="small" color="default" variant="outlined" icon={<LockIcon sx={{ fontSize: 12 }} />} />
              }
            </Stack>
          </Box>
        </Box>
        <Stack direction="row" spacing={1}>
          <Tooltip title="Download least-privilege policy document">
            <Button size="small" variant="outlined" startIcon={<DownloadIcon />}
              href={policyUrl} target="_blank" rel="noopener noreferrer">
              Policy
            </Button>
          </Tooltip>
          <Button
            size="small"
            variant={hasRemediation ? 'outlined' : 'contained'}
            startIcon={<AddCircleOutlineIcon />}
            onClick={() => setExpanded(p => !p)}
          >
            {hasRemediation ? 'Rotate Credentials' : 'Add Auto-Fix Credentials'}
          </Button>
        </Stack>
      </Box>

      <Collapse in={expanded}>
        <Divider sx={{ my: 2 }} />
        {saving ? (
          <Box textAlign="center" py={2}><CircularProgress size={24} /></Box>
        ) : (
          <>
            {provider === 'aws'   && <AWSForm customerId={customerId} onSave={(result) => { setResult(result); if (result.success) { setExpanded(false); onSaved(); } }} />}
            {provider === 'gcp'   && <GCPForm   onSave={handleSave} />}
            {provider === 'azure' && <AzureForm onSave={handleSave} />}
            {provider === 'ssh'   && <SSHForm   onSave={handleSave} />}
            {result && (
              <Alert severity={result.success ? 'success' : 'error'} sx={{ mt: 2 }}>{result.message}</Alert>
            )}
          </>
        )}
      </Collapse>
    </Paper>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function Settings() {
  const { user } = useAuth();
  const customerId = user?.customer_id ?? '';

  const [status,  setStatus]  = useState<RemediationStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState<string | null>(null);

  async function fetchStatus() {
    if (!customerId) return;
    setLoading(true);
    try {
      const { data } = await axios.get<RemediationStatus>(
        `${API_BASE}/v1/autofix/${customerId}/remediation-status`,
      );
      setStatus(data);
    } catch (e: any) {
      setError(e.response?.data?.detail ?? 'Failed to load settings');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { fetchStatus(); }, [customerId]); // eslint-disable-line

  return (
    <Box>
      <Box mb={3}>
        <Typography variant="h4">Cloud Credential Settings</Typography>
        <Typography variant="body2" color="text.secondary" mt={0.5}>
          Manage the read-only scan credentials and optional write-only Auto-Fix credentials per provider.
        </Typography>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {loading ? (
        <Box textAlign="center" py={6}><CircularProgress /></Box>
      ) : (
        <>
          {status?.remediation_enabled ? (
            <Alert severity="success" icon={<CheckCircleIcon />} sx={{ mb: 2 }}>
              Auto-Fix is active — Shield can automatically remediate Tier-1 security issues.
            </Alert>
          ) : (
            <Alert severity="info" sx={{ mb: 2 }}>
              Auto-Fix is not yet configured. Add remediation credentials for one or more providers below
              to let Shield fix issues automatically. You can skip this and Shield will still provide
              step-by-step guided instructions.
            </Alert>
          )}

          {PROVIDERS.map(provider => (
            <ProviderRow
              key={provider}
              provider={provider}
              hasRemediation={status?.providers[provider] ?? false}
              customerId={customerId}
              onSaved={fetchStatus}
            />
          ))}
        </>
      )}
    </Box>
  );
}
